import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  Outlet,
  Link,
} from "react-router-dom";
import { RequirePortalSession } from "./components/Portal/PortalSession";
import Header from "./components/Layout/Header";
import TourOverlay from "./components/Tour/TourOverlay";
import "./App.css";
import PortalLayout, { PortalGate } from "./components/Portal/PortalLayout";
import { ConnectPage, JobsPage, FilesPage, UsagePage, KeysPage, AccountPage, HelpPage } from "./components/Portal/PortalPages";

// Lazy load the components
// Lazy loading is a technique that defers loading of non-critical resources at page load time.
// Instead of loading all components immediately, React.lazy() creates a dynamic import that
// only loads the component when it's actually needed. This improves initial page load performance
// by reducing the initial bundle size and only loading code when the user navigates to that route.
const Layout = React.lazy(() => import("./components/Layout/Layout"));
const PortalLogin = React.lazy(() => import("./components/Portal/PortalLogin"));
const WebsiteDemo = React.lazy(() => import("./components/Portal/WebsiteDemo"));
const WorkspaceProjects = React.lazy(() =>
  import("./components/Pages/WorkspaceProjects")
);
const AuthCallback = React.lazy(() =>
  import("./components/UserAccount/AuthCallback")
);
const Docking = React.lazy(() => import("./components/Docking/Docking"));
const Files = React.lazy(() => import("./components/FileManagement/Files"));
const MDScaleway = React.lazy(() =>
  import("./components/MDSimulation/MDScaleway")
);
const MDResults = React.lazy(() =>
  import("./components/MDSimulation/MDResults")
);
const DockingResults = React.lazy(() =>
  import("./components/Docking/DockingResults")
);
const UnderConstruction = React.lazy(() =>
  import("./components/Pages/UnderConstruction")
);
const Updating = React.lazy(() => import("./components/Pages/Updating"));
const BindingSitePrediction = React.lazy(() =>
  import("./components/Pages/BindingSitePrediction")
);
const Admin = React.lazy(() => import("./components/Admin/Admin"));
const MMPBSA = React.lazy(() => import("./components/Pages/MMPBSA"));
const PdbHealthReport = React.lazy(() =>
  import("./components/Pages/PdbHealthReport")
);
const ToolsHub = React.lazy(() => import("./components/Pages/ToolsHub"));
const ToolLanding = React.lazy(() => import("./components/Pages/ToolLanding"));

const LegacyFrame = () => <><Header /><div className="sa-legacy-banner"><span>Legacy interface · Existing setup forms and viewers</span><Link to="/connect">Return to portal →</Link></div><TourOverlay /><Outlet /></>;

const App = () => {
  useEffect(() => {
    // Check if user has an old version and force refresh if needed
    const checkAppVersion = () => {
      // Only run version check in production
      if (
        process.env.NODE_ENV !== "production" ||
        !process.env.REACT_APP_BUILD_TIME
      ) {
        console.log("Skipping version check in development mode");
        return;
      }

      const storedVersion = localStorage.getItem("appVersion");

      // Use build time from environment variable or current time as fallback
      // const buildTime = process.env.REACT_APP_BUILD_TIME
      //   ? parseInt(process.env.REACT_APP_BUILD_TIME)
      //   : Date.now();

      const buildTime = parseInt(process.env.REACT_APP_BUILD_TIME);

      if (storedVersion) {
        const storedTime = parseInt(storedVersion);
        // If stored version is older than current build, force refresh
        if (storedTime < buildTime) {
          console.log("Old app version detected, forcing refresh...");
          console.log("Stored version:", new Date(storedTime).toISOString());
          console.log("Current build:", new Date(buildTime).toISOString());
          localStorage.setItem("appVersion", buildTime.toString());
          window.location.reload(true);
          return;
        }
      } else {
        // No version stored, set current version
        localStorage.setItem("appVersion", buildTime.toString());
        console.log("App version set to:", new Date(buildTime).toISOString());
      }
    };

    checkAppVersion();
  }, []);

  return (
    <div className="App">
      <Router>
        <Suspense fallback={<div>Loading...</div>}>
          <Routes>
            <Route path="/login" element={<PortalLogin />} />
            <Route path="/dev/login" element={<Navigate to="/login" replace />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/demo" element={<WebsiteDemo />}>
              <Route index element={<ConnectPage />} />
              <Route path="connect" element={<ConnectPage />} />
              <Route path="connect/api" element={<KeysPage />} />
              <Route path="jobs" element={<JobsPage />} />
              <Route path="files" element={<FilesPage />} />
              <Route path="usage" element={<UsagePage />} />
              <Route path="billing" element={<Navigate to="/demo/usage" replace />} />
              <Route path="account" element={<AccountPage />} />
              <Route path="help" element={<HelpPage />} />
            </Route>
            <Route element={<RequirePortalSession />}>
            <Route element={<PortalLayout />}>
              <Route path="/" element={<ConnectPage />} />
              <Route path="/connect" element={<ConnectPage />} />
              <Route path="/connect/api" element={<KeysPage />} />
              <Route path="/jobs" element={<JobsPage />} />
              <Route path="/files" element={<FilesPage />} />
              <Route path="/usage" element={<UsagePage />} />
              <Route path="/billing" element={<Navigate to="/usage" replace />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/admin" element={<PortalGate allowPreview={false}><Admin /></PortalGate>} />
            </Route>
            {/* Dashboard routes */}
            <Route element={<LegacyFrame />}>
            <Route path="/legacy" element={<WorkspaceProjects />} />
            <Route path="/layout" element={<Navigate to="/legacy" replace />} />

            {/* Workspace routes with Dashboard layout */}
            <Route path="/layout/:workspaceId/:projectId" element={<Layout />}>
              <Route path="files" element={<Files />} />
              <Route path="docking" element={<Docking />} />
              <Route path="simulation" element={<MDScaleway />} />
              <Route
                path="binding-site-prediction"
                element={<BindingSitePrediction />}
              />
              <Route path="analysis" element={<MDResults />} />
              <Route path="mmpbsa" element={<MMPBSA />} />
              <Route
                path="docking-analysis-dashboard"
                element={<DockingResults />}
              />
              <Route path="user-profile" element={<Navigate to="/account" replace />} />
              <Route path="usage" element={<Navigate to="/usage" replace />} />
              <Route path="billing" element={<Navigate to="/usage" replace />} />
              <Route path="api" element={<Navigate to="/connect/api" replace />} />
              <Route path="tools-hub" element={<ToolsHub />} />
              <Route path="tools/:toolSlug" element={<ToolLanding />} />
              <Route path="admin" element={<Admin />} />
            </Route>

            </Route>
            </Route>
            <Route path="/under-construction" element={<UnderConstruction />} />
            <Route path="/report/:reportId" element={<PdbHealthReport />} />
            {/* Catch all route */}
            <Route path="*" element={<Updating />} />
          </Routes>
        </Suspense>
      </Router>

      {/* Stream Chat Widget */}
      {/* <StreamChatWidget /> */}
    </div>
  );
};

export default App;
